import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

type Modal = {
  isActive: boolean
  data: any
}

type ModalsState = Record<string, Modal>

const createModalState = (args?: Modal) => ({
  isActive: false,
  data: {},
  ...args,
})

const initialState: ModalsState = {}

export const modalsV2 = createSlice({
  name: 'modalsV2',
  initialState,
  reducers: {
    openModal: (
      state,
      action: PayloadAction<{
        id: string
        data?: any
      }>
    ) => {
      const { id, data = {} } = action.payload
      state[id] = {
        isActive: true,
        data,
      }
    },
    closeModal: (
      state,
      action: PayloadAction<{
        id: string
        keepData?: boolean
      }>
    ) => {
      const { id, keepData = false } = action.payload

      if (!state[id]) state[id] = createModalState()

      state[id].isActive = false

      if (!keepData) {
        state[id].data = {}
      }
    },
  },
})

const selectSelf = (state) => state.modalsV2 as ModalsState

export const selectModal = createSelector(
  [selectSelf, (_, id: string) => id],
  (state, id) => state[id] || createModalState()
)

export const useModal = <T extends unknown>(id: string) => {
  const dispatch = useDispatch()

  const state = useSelector((state) => selectModal(state, id))

  const { isActive, data } = useMemo(
    () => ({ isActive: state.isActive, data: state.data as Partial<T> }),
    [state]
  )

  const open = useCallback(
    (data?: T) => {
      dispatch(openModal({ id, data }))
    },
    [id]
  )
  const onClose = useCallback(
    (keepData?: boolean) => {
      dispatch(closeModal({ id, keepData }))
    },
    [id]
  )

  return {
    isActive,
    data,
    open,
    onClose,
  }
}

export const { openModal, closeModal } = modalsV2.actions

export default modalsV2.reducer
